import { cn } from '@/common/lib/cn/cn'

type DetailedBrandComparisonBlockProgressCellProps = {
    percent: number
    display: string
    color: string
    label: string
}

export const DetailedBrandComparisonBlockProgressCell = ({
    percent,
    display,
    color,
    label,
}: DetailedBrandComparisonBlockProgressCellProps) => {
    const clampedPercent = Math.min(100, Math.max(0, percent))

    return (
        <div className='flex items-center gap-2 min-w-[5.5rem]'>
            <div
                className='h-1 min-w-[2.5rem] flex-1 overflow-hidden rounded-full bg-white/10'
                role='progressbar'
                aria-valuenow={Math.round(clampedPercent)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${label}: ${display}`}
            >
                <div
                    className='h-full rounded-full transition-all'
                    style={{ width: `${clampedPercent}%`, backgroundColor: color }}
                />
            </div>
            <span className='shrink-0 text-xs tabular-nums text-base-content/80'>{display}</span>
        </div>
    )
}

type DetailedBrandComparisonPresenceCellProps = {
    display: string
    percent: number
    label: string
    format: 'percent' | 'count'
}

const presenceTierClass = (percent: number): string => {
    if (percent >= 75) return 'bg-[#08AC81]/15 text-[#08AC81]'
    if (percent >= 40) return 'bg-[#F59E0B]/15 text-[#F59E0B]'
    if (percent > 0) return 'bg-[#FF484B]/15 text-[#FF484B]'
    return 'bg-white/5 text-base-content/40'
}

export const DetailedBrandComparisonPresenceCell = ({
    display,
    percent,
    label,
    format,
}: DetailedBrandComparisonPresenceCellProps) => {
    if (format === 'count' && display === '0') {
        return (
            <span className='text-base-content/40' aria-label={`${label}: 0`}>
                0
            </span>
        )
    }

    if (format === 'percent' && percent === 0) {
        return (
            <span
                className={cn(
                    'inline-flex items-center justify-center rounded px-2 py-0.5 text-xs font-medium tabular-nums',
                    presenceTierClass(0)
                )}
                aria-label={`${label}: 0%`}
            >
                0%
            </span>
        )
    }

    return (
        <span
            className={cn(
                'inline-flex items-center justify-center rounded px-2 py-0.5 text-xs font-medium tabular-nums',
                format === 'percent' ? presenceTierClass(percent) : 'text-base-content/80'
            )}
            aria-label={`${label}: ${display}`}
        >
            {display}
        </span>
    )
}
